import { useEffect, useState } from 'react'
import {
  MagnifyingGlassIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  CalendarIcon,
  GlobeAltIcon,
  LockClosedIcon,
  CurrencyEuroIcon,
  CheckBadgeIcon,
  ChatBubbleLeftIcon,
  StarIcon,
} from '@heroicons/react/24/outline'
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid'
import { Card, Badge, InlineSpinner, EmptyState, Button, Modal, ModalBody, ModalFooter, Pagination } from '@/components/ui'
import apiClient from '@/api/client'
import type { Event } from '@/types'

export default function AdminEventsPage() {
  const [events, setEvents] = useState<Event[]>([])
  const [loading, setLoading] = useState(true)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalItems, setTotalItems] = useState(0)
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState('')

  // Delete Modal
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [eventToDelete, setEventToDelete] = useState<Event | null>(null)
  const [deleting, setDeleting] = useState(false)

  // Create Modal
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [creating, setCreating] = useState(false)
  const [createError, setCreateError] = useState('')
  const [createForm, setCreateForm] = useState({
    title: '',
    short_description: '',
    description: '',
    type: 'webinar',
    status: 'draft',
    start_datetime: '',
    end_datetime: '',
    is_online: true,
    location: '',
    max_participants: '',
    is_public: true,
    is_free: true,
  })

  // Feedback Modal
  const [showFeedbackModal, setShowFeedbackModal] = useState(false)
  const [feedbackEvent, setFeedbackEvent] = useState<Event | null>(null)
  const [feedbacks, setFeedbacks] = useState<Array<{
    id: number
    user_name: string
    overall_rating: number
    content_rating: number | null
    organization_rating: number | null
    comments: string
    would_recommend: boolean
    created_at: string
  }>>([])
  const [loadingFeedback, setLoadingFeedback] = useState(false)

  // Edit Modal
  const [showEditModal, setShowEditModal] = useState(false)
  const [editing, setEditing] = useState(false)
  const [editError, setEditError] = useState('')
  const [eventToEdit, setEventToEdit] = useState<Event | null>(null)
  const [editForm, setEditForm] = useState({
    title: '',
    short_description: '',
    description: '',
    type: 'webinar',
    status: 'draft',
    start_datetime: '',
    end_datetime: '',
    is_online: true,
    location: '',
    max_participants: '',
    is_public: true,
    is_free: true,
  })

  useEffect(() => {
    fetchEvents()
  }, [currentPage, searchQuery, statusFilter])

  const fetchEvents = async () => {
    setLoading(true)
    try {
      const response = await apiClient.get('/admin/events/', {
        params: {
          page: currentPage,
          search: searchQuery || undefined,
          status: statusFilter || undefined,
        },
      })
      setEvents(response.data.results || response.data)
      setTotalPages(Math.ceil((response.data.count || response.data.length) / 20))
      setTotalItems(response.data.count || response.data.length)
    } catch (error) {
      console.error('Error fetching events:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleDeleteEvent = async () => {
    if (!eventToDelete) return
    setDeleting(true)
    try {
      await apiClient.delete(`/admin/events/${eventToDelete.id}/`)
      setShowDeleteModal(false)
      setEventToDelete(null)
      fetchEvents()
    } catch (error) {
      console.error('Error deleting event:', error)
    } finally {
      setDeleting(false)
    }
  }

  const handleOpenEdit = (event: Event) => {
    setEventToEdit(event)
    // Format datetime for input fields (remove timezone info)
    const formatDateForInput = (dateString: string) => {
      const date = new Date(dateString)
      return date.toISOString().slice(0, 16)
    }
    setEditForm({
      title: event.title,
      short_description: event.short_description || '',
      description: event.description || '',
      type: event.type,
      status: event.status,
      start_datetime: formatDateForInput(event.start_datetime),
      end_datetime: formatDateForInput(event.end_datetime),
      is_online: event.is_online,
      location: event.location || '',
      max_participants: event.max_participants ? String(event.max_participants) : '',
      is_public: event.is_public ?? true,
      is_free: event.is_free ?? true,
    })
    setEditError('')
    setShowEditModal(true)
  }

  const handleEditEvent = async () => {
    if (!eventToEdit) return
    setEditing(true)
    setEditError('')
    try {
      const payload = {
        ...editForm,
        max_participants: editForm.max_participants ? parseInt(editForm.max_participants) : null,
      }
      await apiClient.patch(`/admin/events/${eventToEdit.id}/`, payload)
      setShowEditModal(false)
      setEventToEdit(null)
      fetchEvents()
    } catch (error: any) {
      console.error('Error updating event:', error)
      if (error.response?.data) {
        const data = error.response.data
        if (typeof data === 'object') {
          const messages = Object.entries(data)
            .map(([field, errors]) => `${field}: ${Array.isArray(errors) ? errors.join(', ') : errors}`)
            .join('; ')
          setEditError(messages)
        } else {
          setEditError(String(data))
        }
      } else {
        setEditError('Errore durante l\'aggiornamento dell\'evento')
      }
    } finally {
      setEditing(false)
    }
  }

  const handleCreateEvent = async () => {
    setCreating(true)
    setCreateError('')
    try {
      const payload = {
        ...createForm,
        max_participants: createForm.max_participants ? parseInt(createForm.max_participants) : null,
      }
      await apiClient.post('/admin/events/', payload)
      setShowCreateModal(false)
      setCreateForm({
        title: '',
        short_description: '',
        description: '',
        type: 'webinar',
        status: 'draft',
        start_datetime: '',
        end_datetime: '',
        is_online: true,
        location: '',
        max_participants: '',
        is_public: true,
        is_free: true,
      })
      fetchEvents()
    } catch (error: any) {
      console.error('Error creating event:', error)
      if (error.response?.data) {
        const data = error.response.data
        if (typeof data === 'object') {
          const messages = Object.entries(data)
            .map(([field, errors]) => `${field}: ${Array.isArray(errors) ? errors.join(', ') : errors}`)
            .join('; ')
          setCreateError(messages)
        } else {
          setCreateError(String(data))
        }
      } else {
        setCreateError('Errore durante la creazione dell\'evento')
      }
    } finally {
      setCreating(false)
    }
  }

  const handleOpenFeedback = async (event: Event) => {
    setFeedbackEvent(event)
    setShowFeedbackModal(true)
    setLoadingFeedback(true)
    try {
      const response = await apiClient.get(`/events/events/${event.slug}/feedback/`)
      setFeedbacks(response.data)
    } catch (error: any) {
      console.error('Error fetching feedback:', error)
      setFeedbacks([])
    } finally {
      setLoadingFeedback(false)
    }
  }

  const getStatusBadge = (status: string) => {
    const config: Record<string, { variant: 'default' | 'info' | 'warning' | 'success' | 'danger'; label: string }> = {
      draft: { variant: 'default', label: 'Bozza' },
      published: { variant: 'success', label: 'Pubblicato' },
      cancelled: { variant: 'danger', label: 'Annullato' },
      completed: { variant: 'info', label: 'Completato' },
    }
    return config[status] || { variant: 'default', label: status }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('it-IT', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  if (loading && currentPage === 1) {
    return <InlineSpinner message="Caricamento eventi..." />
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gestione Eventi</h1>
          <p className="text-gray-600">{totalItems} eventi</p>
        </div>
        <Button onClick={() => setShowCreateModal(true)}>
          <PlusIcon className="h-5 w-5 mr-2" />
          Nuovo Evento
        </Button>
      </div>

      {/* Search and Filters */}
      <Card>
        <div className="flex flex-wrap gap-4">
          <div className="flex-1 min-w-[250px] relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Cerca eventi..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
          >
            <option value="">Tutti gli stati</option>
            <option value="draft">Bozza</option>
            <option value="published">Pubblicato</option>
            <option value="cancelled">Annullato</option>
            <option value="completed">Completato</option>
          </select>
        </div>
      </Card>

      {/* Events Table */}
      {events.length > 0 ? (
        <>
          <Card className="overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50">
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Evento</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tipo</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Data</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Stato</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Iscritti</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Azioni</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {events.map((event) => {
                    const statusConfig = getStatusBadge(event.status)
                    return (
                      <tr key={event.id} className="hover:bg-gray-50">
                        <td className="px-4 py-4">
                          <div>
                            <p className="font-medium text-gray-900">{event.title}</p>
                            <p className="text-sm text-gray-500 truncate max-w-xs">{event.short_description}</p>
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <Badge variant="info">{event.type}</Badge>
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-500">
                          {formatDate(event.start_datetime)}
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex flex-col gap-1">
                            <Badge variant={statusConfig.variant}>{statusConfig.label}</Badge>
                            {event.is_public ? (
                              <Badge variant="info">
                                <GlobeAltIcon className="h-3 w-3 mr-1" />
                                Pubblico
                              </Badge>
                            ) : (
                              <Badge variant="default">
                                <LockClosedIcon className="h-3 w-3 mr-1" />
                                Privato
                              </Badge>
                            )}
                            {event.is_free ? (
                              <Badge variant="success">
                                <CheckBadgeIcon className="h-3 w-3 mr-1" />
                                Gratuito
                              </Badge>
                            ) : (
                              <Badge variant="warning">
                                <CurrencyEuroIcon className="h-3 w-3 mr-1" />
                                A pagamento
                              </Badge>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-500">
                          {event.registrations_count}{event.max_participants ? ` / ${event.max_participants}` : ''}
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => handleOpenFeedback(event)}
                              className="p-1 text-yellow-500 hover:bg-yellow-50 rounded"
                              title="Feedback"
                            >
                              <ChatBubbleLeftIcon className="h-5 w-5" />
                            </button>
                            <button
                              onClick={() => handleOpenEdit(event)}
                              className="p-1 text-gray-500 hover:bg-gray-100 rounded"
                              title="Modifica"
                            >
                              <PencilIcon className="h-5 w-5" />
                            </button>
                            <button
                              onClick={() => { setEventToDelete(event); setShowDeleteModal(true); }}
                              className="p-1 text-red-500 hover:bg-red-50 rounded"
                              title="Elimina"
                            >
                              <TrashIcon className="h-5 w-5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </Card>

          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalItems}
            onPageChange={setCurrentPage}
          />
        </>
      ) : (
        <Card>
          <EmptyState
            icon={<CalendarIcon className="h-12 w-12" />}
            title="Nessun evento"
            description="Non ci sono eventi da visualizzare"
          />
        </Card>
      )}

      {/* Delete Modal */}
      <Modal isOpen={showDeleteModal} onClose={() => setShowDeleteModal(false)} title="Elimina Evento">
        <ModalBody>
          {eventToDelete && (
            <div className="space-y-4">
              <p className="text-gray-600">
                Sei sicuro di voler eliminare l'evento{' '}
                <span className="font-semibold">{eventToDelete.title}</span>?
              </p>
              <p className="text-sm text-red-600">
                Questa azione è irreversibile.
              </p>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" onClick={() => setShowDeleteModal(false)}>Annulla</Button>
          <Button variant="danger" onClick={handleDeleteEvent} disabled={deleting}>
            {deleting ? 'Eliminazione...' : 'Elimina'}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Create Modal */}
      <Modal isOpen={showCreateModal} onClose={() => { setShowCreateModal(false); setCreateError(''); }} title="Nuovo Evento">
        <ModalBody>
          <div className="space-y-4">
            {createError && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                {createError}
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Titolo *</label>
              <input
                type="text"
                value={createForm.title}
                onChange={(e) => setCreateForm({ ...createForm, title: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                placeholder="Nome evento"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Descrizione *</label>
              <textarea
                value={createForm.description}
                onChange={(e) => setCreateForm({ ...createForm, description: e.target.value })}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                placeholder="Descrizione completa dell'evento"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Descrizione breve</label>
              <input
                type="text"
                value={createForm.short_description}
                onChange={(e) => setCreateForm({ ...createForm, short_description: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                placeholder="Breve descrizione (opzionale)"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tipo</label>
              <select
                value={createForm.type}
                onChange={(e) => setCreateForm({ ...createForm, type: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              >
                <option value="webinar">Webinar</option>
                <option value="workshop">Workshop</option>
                <option value="conference">Conferenza</option>
                <option value="community_day">Community Day</option>
                <option value="training">Training</option>
                <option value="meeting">Riunione</option>
                <option value="other">Altro</option>
              </select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Data/ora inizio *</label>
                <input
                  type="datetime-local"
                  value={createForm.start_datetime}
                  onChange={(e) => setCreateForm({ ...createForm, start_datetime: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Data/ora fine *</label>
                <input
                  type="datetime-local"
                  value={createForm.end_datetime}
                  onChange={(e) => setCreateForm({ ...createForm, end_datetime: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                />
              </div>
            </div>
            <div className="space-y-2">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={createForm.is_online}
                  onChange={(e) => setCreateForm({ ...createForm, is_online: e.target.checked })}
                  className="rounded border-gray-300"
                />
                <span className="text-sm text-gray-700">Evento online</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={createForm.is_public}
                  onChange={(e) => setCreateForm({ ...createForm, is_public: e.target.checked })}
                  className="rounded border-gray-300"
                />
                <span className="text-sm text-gray-700">
                  <GlobeAltIcon className="h-4 w-4 inline mr-1" />
                  Pubblico (visibile anche ai Guest)
                </span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={createForm.is_free}
                  onChange={(e) => setCreateForm({ ...createForm, is_free: e.target.checked })}
                  className="rounded border-gray-300"
                />
                <span className="text-sm text-gray-700">
                  <CheckBadgeIcon className="h-4 w-4 inline mr-1" />
                  Gratuito
                </span>
              </label>
            </div>
            {!createForm.is_online && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Luogo</label>
                <input
                  type="text"
                  value={createForm.location}
                  onChange={(e) => setCreateForm({ ...createForm, location: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  placeholder="Indirizzo o luogo"
                />
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Max partecipanti</label>
              <input
                type="number"
                value={createForm.max_participants}
                onChange={(e) => setCreateForm({ ...createForm, max_participants: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                placeholder="Lascia vuoto per illimitati"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Stato</label>
              <select
                value={createForm.status}
                onChange={(e) => setCreateForm({ ...createForm, status: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              >
                <option value="draft">Bozza</option>
                <option value="published">Pubblicato</option>
              </select>
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" onClick={() => { setShowCreateModal(false); setCreateError(''); }}>Annulla</Button>
          <Button onClick={handleCreateEvent} disabled={creating || !createForm.title || !createForm.description || !createForm.start_datetime || !createForm.end_datetime}>
            {creating ? 'Creazione...' : 'Crea Evento'}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Edit Modal */}
      <Modal isOpen={showEditModal} onClose={() => { setShowEditModal(false); setEditError(''); }} title="Modifica Evento">
        <ModalBody>
          <div className="space-y-4">
            {editError && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                {editError}
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Titolo *</label>
              <input
                type="text"
                value={editForm.title}
                onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                placeholder="Nome evento"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Descrizione *</label>
              <textarea
                value={editForm.description}
                onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                placeholder="Descrizione completa dell'evento"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Descrizione breve</label>
              <input
                type="text"
                value={editForm.short_description}
                onChange={(e) => setEditForm({ ...editForm, short_description: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                placeholder="Breve descrizione (opzionale)"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tipo</label>
              <select
                value={editForm.type}
                onChange={(e) => setEditForm({ ...editForm, type: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              >
                <option value="webinar">Webinar</option>
                <option value="workshop">Workshop</option>
                <option value="conference">Conferenza</option>
                <option value="community_day">Community Day</option>
                <option value="training">Training</option>
                <option value="meeting">Riunione</option>
                <option value="other">Altro</option>
              </select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Data/ora inizio *</label>
                <input
                  type="datetime-local"
                  value={editForm.start_datetime}
                  onChange={(e) => setEditForm({ ...editForm, start_datetime: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Data/ora fine *</label>
                <input
                  type="datetime-local"
                  value={editForm.end_datetime}
                  onChange={(e) => setEditForm({ ...editForm, end_datetime: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                />
              </div>
            </div>
            <div className="space-y-2">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={editForm.is_online}
                  onChange={(e) => setEditForm({ ...editForm, is_online: e.target.checked })}
                  className="rounded border-gray-300"
                />
                <span className="text-sm text-gray-700">Evento online</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={editForm.is_public}
                  onChange={(e) => setEditForm({ ...editForm, is_public: e.target.checked })}
                  className="rounded border-gray-300"
                />
                <span className="text-sm text-gray-700">
                  <GlobeAltIcon className="h-4 w-4 inline mr-1" />
                  Pubblico (visibile anche ai Guest)
                </span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={editForm.is_free}
                  onChange={(e) => setEditForm({ ...editForm, is_free: e.target.checked })}
                  className="rounded border-gray-300"
                />
                <span className="text-sm text-gray-700">
                  <CheckBadgeIcon className="h-4 w-4 inline mr-1" />
                  Gratuito
                </span>
              </label>
            </div>
            {!editForm.is_online && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Luogo</label>
                <input
                  type="text"
                  value={editForm.location}
                  onChange={(e) => setEditForm({ ...editForm, location: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  placeholder="Indirizzo o luogo"
                />
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Max partecipanti</label>
              <input
                type="number"
                value={editForm.max_participants}
                onChange={(e) => setEditForm({ ...editForm, max_participants: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                placeholder="Lascia vuoto per illimitati"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Stato</label>
              <select
                value={editForm.status}
                onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              >
                <option value="draft">Bozza</option>
                <option value="published">Pubblicato</option>
                <option value="cancelled">Annullato</option>
                <option value="completed">Completato</option>
              </select>
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" onClick={() => { setShowEditModal(false); setEditError(''); }}>Annulla</Button>
          <Button onClick={handleEditEvent} disabled={editing || !editForm.title || !editForm.description || !editForm.start_datetime || !editForm.end_datetime}>
            {editing ? 'Salvataggio...' : 'Salva Modifiche'}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Feedback Modal */}
      <Modal
        isOpen={showFeedbackModal}
        onClose={() => setShowFeedbackModal(false)}
        title={feedbackEvent ? `Feedback: ${feedbackEvent.title}` : 'Feedback'}
        size="lg"
      >
        <ModalBody>
          {loadingFeedback ? (
            <div className="py-8">
              <InlineSpinner message="Caricamento feedback..." />
            </div>
          ) : feedbacks.length > 0 ? (
            <div className="space-y-4">
              {/* Summary */}
              <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                <div className="text-center">
                  <div className="text-3xl font-bold text-gray-900">
                    {(feedbacks.reduce((acc, f) => acc + f.overall_rating, 0) / feedbacks.length).toFixed(1)}
                  </div>
                  <div className="flex items-center justify-center gap-0.5 mt-1">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <StarIconSolid
                        key={star}
                        className={`h-4 w-4 ${
                          star <= Math.round(feedbacks.reduce((acc, f) => acc + f.overall_rating, 0) / feedbacks.length)
                            ? 'text-yellow-400'
                            : 'text-gray-300'
                        }`}
                      />
                    ))}
                  </div>
                </div>
                <div className="text-sm text-gray-600">
                  <strong>{feedbacks.length}</strong> feedback ricevuti
                  <br />
                  <span className="text-green-600">
                    {feedbacks.filter(f => f.would_recommend).length} consiglierebbero
                  </span>
                </div>
              </div>

              {/* Feedback List */}
              <div className="divide-y divide-gray-200 max-h-96 overflow-y-auto">
                {feedbacks.map((feedback) => (
                  <div key={feedback.id} className="py-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-medium text-gray-900">{feedback.user_name}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <div className="flex items-center gap-0.5">
                            {[1, 2, 3, 4, 5].map((star) => (
                              <StarIconSolid
                                key={star}
                                className={`h-4 w-4 ${
                                  star <= feedback.overall_rating
                                    ? 'text-yellow-400'
                                    : 'text-gray-300'
                                }`}
                              />
                            ))}
                          </div>
                          {feedback.would_recommend && (
                            <Badge variant="success" size="sm">Consiglia</Badge>
                          )}
                        </div>
                      </div>
                      <p className="text-xs text-gray-400">
                        {new Date(feedback.created_at).toLocaleDateString('it-IT')}
                      </p>
                    </div>
                    {(feedback.content_rating || feedback.organization_rating) && (
                      <div className="mt-2 flex gap-4 text-sm text-gray-500">
                        {feedback.content_rating && (
                          <span>Contenuto: {feedback.content_rating}/5</span>
                        )}
                        {feedback.organization_rating && (
                          <span>Organizzazione: {feedback.organization_rating}/5</span>
                        )}
                      </div>
                    )}
                    {feedback.comments && (
                      <p className="mt-2 text-gray-700">{feedback.comments}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="py-8 text-center text-gray-500">
              <StarIcon className="h-12 w-12 mx-auto text-gray-300 mb-2" />
              <p>Nessun feedback per questo evento</p>
              <p className="text-sm mt-1">I feedback sono disponibili solo dopo la conclusione dell'evento</p>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" onClick={() => setShowFeedbackModal(false)}>Chiudi</Button>
        </ModalFooter>
      </Modal>
    </div>
  )
}
